/**
 * Work out what part of an item a photo shows, so each thumbnail can be
 * labelled automatically instead of relying on upload order.
 *
 * Three signals, strongest first:
 *   1. An explicit `angle` stored with the photo (set at upload time).
 *   2. Hints in the file name / URL - sellers overwhelmingly name their shots
 *      "back.jpg", "IMG_side_2.png", "label-close-up.jpeg" and so on.
 *   3. A garment-aware fallback sequence, so a bag never gets a "Sole" tag and
 *      a shoe never gets a "Hem" one.
 */

export type Angle =
  | 'Front' | 'Back' | 'Side' | 'Detail' | 'Tag' | 'Worn'
  | 'Inside' | 'Sole' | 'Strap' | 'Screen' | 'Ports' | 'Flaw';

/** Keyword → angle. Ordered: the first match wins, so put specifics first. */
const HINTS: [RegExp, Angle][] = [
  [/\b(tag|label|care[-_ ]?label|authentic|serial|imei|barcode|hallmark)\b/i, 'Tag'],
  [/\b(flaw|damage|defect|mark|stain|scuff|wear[-_ ]?spot|repair)\b/i,        'Flaw'],
  [/\b(worn|model|on[-_ ]?body|outfit|styled|fit[-_ ]?pic|mirror)\b/i,        'Worn'],
  [/\b(inside|interior|lining|inner|pocket)\b/i,                              'Inside'],
  [/\b(sole|outsole|tread|bottom|underside)\b/i,                              'Sole'],
  [/\b(strap|handle|chain|buckle|clasp|zip|zipper|hardware)\b/i,              'Strap'],
  [/\b(screen|display|front[-_ ]?glass)\b/i,                                  'Screen'],
  [/\b(port|charging|connector|socket|jack)\b/i,                              'Ports'],
  [/\b(detail|close[-_ ]?up|closeup|macro|texture|stitch|fabric|logo)\b/i,    'Detail'],
  [/\b(back|rear|reverse|behind)\b/i,                                         'Back'],
  [/\b(side|profile|left|right|angle)\b/i,                                    'Side'],
  [/\b(front|main|hero|cover|primary|face)\b/i,                               'Front'],
];

/** Per-garment ordering, used when a photo gives us nothing to go on. */
const SEQUENCES: Record<string, Angle[]> = {
  shoes:       ['Front', 'Side', 'Sole', 'Back', 'Detail', 'Tag'],
  bags:        ['Front', 'Back', 'Inside', 'Strap', 'Detail', 'Tag'],
  accessories: ['Front', 'Side', 'Detail', 'Back', 'Tag', 'Worn'],
  watches:     ['Front', 'Side', 'Back', 'Detail', 'Tag', 'Worn'],
  electronics: ['Front', 'Screen', 'Back', 'Ports', 'Detail', 'Tag'],
  default:     ['Front', 'Back', 'Side', 'Detail', 'Tag', 'Worn'],
};

const sequenceFor = (garment?: string, category?: string): Angle[] => {
  const key = `${garment ?? ''} ${category ?? ''}`.toLowerCase();
  if (/shoe|trainer|sneaker|boot|heel|loafer|sandal/.test(key)) return SEQUENCES.shoes;
  if (/bag|tote|clutch|backpack|holdall|purse|wallet/.test(key)) return SEQUENCES.bags;
  if (/watch/.test(key))                                        return SEQUENCES.watches;
  if (/phone|laptop|tablet|camera|electronic|tech|headphone/.test(key)) return SEQUENCES.electronics;
  if (/accessor|jewell|scarf|belt|sunglass|hat/.test(key))      return SEQUENCES.accessories;
  return SEQUENCES.default;
};

/** Pull an angle out of a file name / URL, if the seller named it usefully. */
export function angleFromFilename(src: string): Angle | null {
  // Strip query strings and decode, then look at the last path segment.
  const name = decodeURIComponent((src.split('?')[0].split('/').pop() ?? '')).replace(/[._-]+/g, ' ');
  for (const [re, angle] of HINTS) if (re.test(name)) return angle;
  return null;
}

/**
 * Final label for one photo.
 * @param src      photo URL
 * @param index    its position in the gallery
 * @param opts     garment/category for a sensible fallback, plus any stored angle
 */
export function angleFor(
  src: string,
  index: number,
  opts: { garment?: string; category?: string; stored?: string | null; used?: Set<string> } = {},
): Angle {
  // 1. Explicitly stored at upload time.
  if (opts.stored) return opts.stored as Angle;

  // 2. Named by the seller.
  const named = angleFromFilename(src);
  if (named && !opts.used?.has(named)) return named;

  // 3. Garment-aware fallback - skip labels already taken so tags stay unique.
  const seq = sequenceFor(opts.garment, opts.category);
  const free = seq.filter((a) => !opts.used?.has(a));
  return free[0] ?? seq[index % seq.length];
}

/** Label every photo in a gallery, keeping the tags unique and in order. */
export function angleLabels(
  images: string[],
  opts: { garment?: string; category?: string; stored?: (string | null | undefined)[] } = {},
): Angle[] {
  const used = new Set<string>();
  return images.map((src, i) => {
    const angle = angleFor(src, i, {
      garment: opts.garment,
      category: opts.category,
      stored: opts.stored?.[i] ?? null,
      used,
    });
    used.add(angle);
    return angle;
  });
}
